import {
  breakpointSizes,
  Checkbox,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AppName from 'src/components/AppName/AppName'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import TestId from 'src/components/TestId/TestId'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import {
  OrganizationInviteModel,
  OrganizationInviteResponse,
} from 'src/models/organizations.models'
import {
  orgInviteDecisionUrl,
  peopleMembersDetailUrl,
  privacyUrl,
  termsUrl,
} from 'src/navigation/urls'
import { createSetPerSessionValues } from 'src/redux/actions/auth.actions'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

interface OrgInvitationFormProps {
  organizationInvite: OrganizationInviteResponse
  onOrgInvitationFormSubmit: (val: boolean) => void
  patchingOrganizationInvite: boolean
  patchingOrganizationInviteTopLevelError?: string
  creditCards: CreditCardListModel[]
  // commenting out for now. May restore later
  // virtualCardId?: string
}

const OrgInvitationForm: React.FC<OrgInvitationFormProps> = (props) => {
  const userId = useSelector(selectUserIdFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const buildingsImageSrc = useColorOrGrayscaleImage(ImageNames.Buildings)

  const [confirmTrustInviteeEmail, setConfirmTrustInviteeEmail] =
    useState(false)
  const [confirmTrustInviteeEmailError, setConfirmTrustInviteeEmailError] =
    useState(false)
  const [confirmAccountAddedToOrg, setConfirmAccountAddedToOrg] =
    useState(false)
  const [confirmAccountAddedToOrgError, setConfirmAccountAddedToOrgError] =
    useState(false)
  const [confirmTermsAndConditions, setConfirmTermsAndConditions] =
    useState(false)
  const [confirmTermsAndConditionsError, setConfirmTermsAndConditionsError] =
    useState(false)

  // commenting out for now. May restore later
  // const virtualCard = props.virtualCardId
  //   ? Object.values(virtualCards).filter((vc) => vc.id === props.virtualCardId)[0]
  //   : undefined

  const dispatch = useDispatch()

  const onOrgInviteDecisionScreen =
    calculatedBackgroundLocation.pathname === orgInviteDecisionUrl()

  const {
    patchingOrganizationInvite,
    patchingOrganizationInviteTopLevelError,
    creditCards,
    organizationInvite: { inviter, organization },
  } = props
  const myCards = creditCards.filter((card) => card.user.id === userId)
  const orgInvite = new OrganizationInviteModel(props.organizationInvite)

  const onOrgInvitationFormSubmit = (): void => {
    if (!confirmTrustInviteeEmail) {
      setConfirmTrustInviteeEmailError(true)
    }

    if (!confirmTermsAndConditions && onOrgInviteDecisionScreen) {
      setConfirmTermsAndConditionsError(true)
    }

    if (!confirmAccountAddedToOrg && myCards.length > 0) {
      setConfirmAccountAddedToOrgError(true)
    }

    if (
      confirmTrustInviteeEmail &&
      (confirmTermsAndConditions || !onOrgInviteDecisionScreen) &&
      (confirmAccountAddedToOrg || myCards.length === 0)
    ) {
      props.onOrgInvitationFormSubmit(true)
      dispatch(
        createSetPerSessionValues({ isAcceptOrgInvitePopupDismissed: true }),
      )
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        width: 700px;
        position: relative;
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          width: 100%;
        `)}
      `}
    >
      <img
        src={buildingsImageSrc}
        alt=""
        css={css`
          position: absolute;
          top: 0;
          right: 0;
          border-top-right-radius: 10px;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            display: none;
          `)}
        `}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          padding: 60px 60px 40px 60px;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          <Trns source={"You've been invited to"} />
        </span>
        <span
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.highlight,
              styleConstants.fontFamilies.medium,
            )};
            padding-right: 30px;
          `}
        >
          {organization.name}
        </span>
        <span
          css={css`
            margin-top: 40px;
            margin-bottom: 20px;
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <Trns
            source={
              '${inviterLink} ${inviterEmail} has invited you to join ${organization} in ${appName}. You will join with the role ${role}:'
            }
            expressions={{
              inviterLink: (
                <HoverableLink
                  to={peopleMembersDetailUrl(inviter.id)}
                  ariaLabelSource={inviter.email}
                  underline
                  size={HoverableLinkSizes.Large}
                >
                  {`${inviter.firstName} ${inviter.lastName}`}
                </HoverableLink>
              ),
              inviterEmail: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  {`(${inviter.email})`}
                </span>
              ),
              organization: organization.name,
              role: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  <Trns source={orgInvite.orgRoleDisplayTitle} />
                </span>
              ),
              appName: <AppName />,
            }}
          />
        </span>
        <div
          css={css`
            border-radius: ${styleConstants.borderRadius.box};
            border: 1px solid ${styleConstants.colors.gray_medium};
            display: flex;
            flex-direction: column;
            padding: 20px;
            margin-bottom: 30px;
            background-color: ${styleConstants.colors.gray_lightest};
          `}
        >
          <span
            css={css`
              margin-bottom: 6px;
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            <Trns
              source={'${roles} can...'}
              expressions={{
                roles: <Trns source={orgInvite.orgRoleDisplayTitlePlural} />,
              }}
            />
          </span>
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            <Trns
              source={
                orgInvite.isAdmin
                  ? 'Invite users to the org and manage roles. Admins can configure expense policies and have full visibility across org'
                  : orgInvite.isCardManager
                    ? "Register select accounts for virtual card and budget creation. May be given select edit access to accounts at Owner's discretion."
                    : orgInvite.isBookkeeper
                      ? 'Access extra tools to manage spend and reconciliation. Bookkeepers have selective visibility to accounts managed within the org.'
                      : 'See and interact with all org members'
              }
            />
          </span>
        </div>
        <span
          css={css`
            margin-bottom: 15px;
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          <Trns source={'Please confirm'} />
        </span>
        <div
          css={css`
            display: flex;
          `}
        >
          <TestId testId={'confirmTrustInviteeEmail'}>
            <Checkbox
              onClick={(): void => {
                setConfirmTrustInviteeEmail(!confirmTrustInviteeEmail)
                setConfirmTrustInviteeEmailError(false)
              }}
              isSelected={confirmTrustInviteeEmail}
              isRequired={confirmTrustInviteeEmailError}
            />
          </TestId>
          <span
            css={css`
              margin-left: 12px;
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
            `}
          >
            <Trns
              source={'${inviterLink} ${inviterEmail} is someone you trust.'}
              expressions={{
                inviterLink: (
                  <HoverableLink
                    to={peopleMembersDetailUrl(inviter.id)}
                    ariaLabelSource={inviter.email}
                    underline
                    size={HoverableLinkSizes.Large}
                  >
                    {`${inviter.firstName} ${inviter.lastName}`}
                  </HoverableLink>
                ),
                inviterEmail: (
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )}
                    `}
                  >
                    {`(${inviter.email})`}
                  </span>
                ),
              }}
            />
          </span>
        </div>
        {myCards.length >= 1 && (
          <div
            css={css`
              display: flex;
              margin-top: 20px;
            `}
          >
            <TestId testId={'confirmAccountAddedToOrg'}>
              <Checkbox
                onClick={(): void => {
                  setConfirmAccountAddedToOrg(!confirmAccountAddedToOrg)
                  setConfirmAccountAddedToOrgError(false)
                }}
                isSelected={confirmAccountAddedToOrg}
                isRequired={confirmAccountAddedToOrgError}
                css={css`
                  align-items: start;
                `}
              />
            </TestId>
            <span
              css={css`
                margin-left: 12px;
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              <Trns
                source={
                  '${bold} on the following accounts. In addition, any expense categories you have set up on those accounts will be overwritten.'
                }
                expressions={{
                  bold: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )}
                      `}
                    >
                      <Trns
                        source={
                          'You understand that org owner, admins, and bookkeepers will have view permissions'
                        }
                      />
                    </span>
                  ),
                }}
              />
            </span>
          </div>
        )}
        {myCards.map((card) => (
          <div
            key={card.id}
            css={css`
              border-radius: ${styleConstants.borderRadius.box};
              border: 1px solid ${styleConstants.colors.gray_medium};
              display: flex;
              padding: 20px;
              margin-top: 20px;
              background-color: ${styleConstants.colors.gray_lightest};
            `}
          >
            <img
              src={card.cardImage.urls.small}
              alt={''}
              css={css`
                width: 32px;
                height: 20px;
                margin-right: 8px;
              `}
            />
            <span
              css={css`
                display: inline-block;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {card.displayName}
            </span>
            <div
              css={css`
                border: 1px solid ${styleConstants.colors.gray_medium};
                border-radius: 5px;
                background-color: ${styleConstants.colors.gray_medium};
                padding: 4px;
                display: flex;
                margin-left: 8px;
              `}
            >
              <span
                css={css`
                  ${getFontCss(
                    10,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {card.last4}
              </span>
            </div>
          </div>
        ))}
        {onOrgInviteDecisionScreen && (
          <div
            css={css`
              display: flex;
              margin-top: 20px;
            `}
          >
            <TestId testId={'confirmTermsAndConditions'}>
              <Checkbox
                onClick={(): void => {
                  setConfirmTermsAndConditions(!confirmTermsAndConditions)
                  setConfirmTermsAndConditionsError(false)
                }}
                isSelected={confirmTermsAndConditions}
                isRequired={confirmTermsAndConditionsError}
              />
            </TestId>
            <span
              css={css`
                margin-left: 12px;
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              <Trns
                source={
                  '${bolded}. Your information is secure. We neither sell nor publicize your information outside of ${appName}.'
                }
                expressions={{
                  bolded: (
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      <Trns
                        source={
                          'You agree to our ${termsAndConditions} and ${privacyPolicy}'
                        }
                        expressions={{
                          termsAndConditions: (
                            <a
                              href={termsUrl()}
                              target={'_blank'}
                              rel="noopener noreferrer"
                              css={css`
                                text-decoration: underline;
                              `}
                            >
                              <Trns source={'Terms and Conditions'} />
                            </a>
                          ),
                          privacyPolicy: (
                            <a
                              href={privacyUrl()}
                              target={'_blank'}
                              rel="noopener noreferrer"
                              css={css`
                                text-decoration: underline;
                              `}
                            >
                              <Trns source={'Privacy Policy'} />
                            </a>
                          ),
                        }}
                      />
                    </span>
                  ),
                  appName: <AppName />,
                }}
              />
            </span>
          </div>
        )}
        {(confirmTrustInviteeEmailError ||
          confirmAccountAddedToOrgError ||
          confirmTermsAndConditionsError ||
          patchingOrganizationInviteTopLevelError) && (
          <ErrorList
            errors={[
              confirmTrustInviteeEmailError ||
              confirmAccountAddedToOrgError ||
              confirmTermsAndConditionsError ? (
                <Trns source={'Please confirm the above before accepting'} />
              ) : (
                ''
              ),
              patchingOrganizationInviteTopLevelError || '',
            ]}
            css={css`
              margin-top: 30px;
            `}
          />
        )}
        <div
          css={css`
            display: flex;
            align-items: center;
            margin-top: ${patchingOrganizationInviteTopLevelError ||
            confirmTrustInviteeEmailError ||
            confirmAccountAddedToOrgError ||
            confirmTermsAndConditionsError
              ? '10px'
              : '40px'};
          `}
        >
          {/* accept invite */}
          <Button
            type={ButtonTypes.button}
            design={ButtonDesigns.designCta}
            big={true}
            text={<Trns source={'Accept and Join'} />}
            onClick={onOrgInvitationFormSubmit}
            loading={patchingOrganizationInvite}
          />
          {/* decline invite */}
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.red,
                styleConstants.fontFamilies.regular,
              )};
              text-decoration: underline;
              cursor: pointer;
              margin-left: 20px;
            `}
            onClick={(): void => {
              props.onOrgInvitationFormSubmit(false)
              // commenting out for now. May restore later
              // if (virtualCard) {
              //   dispatch(putVirtualCardCancelAndCloseModalOrRedirect(virtualCard.id, virtualCard.isPending, true))
              // }
              dispatch(
                createSetPerSessionValues({
                  isAcceptOrgInvitePopupDismissed: true,
                }),
              )
            }}
            onKeyPress={(): null => {
              return null
            }}
            role={'button'}
            tabIndex={0}
          >
            <Trns source={'Decline invitation'} />
          </span>
        </div>
      </div>
      <span
        css={css`
          ${getFontCss(
            16,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )};
          padding: 40px 60px;
          background-color: ${styleConstants.colors.gray_light};
          border-radius: 0px 0px 10px 10px;
        `}
      >
        <Trns
          source={'Accepting this invitation auto-declines other invitations.'}
        />
      </span>
    </div>
  )
}

export default OrgInvitationForm
